import { useCallback, useEffect, useRef, useState } from 'react'

const SPEECH_LANGS = { de: 'de-DE', en: 'en-US', nl: 'nl-NL' }

// Web Speech API with continuous mode + auto-restart so a pause (or Chrome's ~60s session cap) doesn't kill recording.
// `active=false` aborts an in-flight session and clears any stale mic-denied alert (use it to mirror an outer "is the host UI open" flag).
// `onTranscript` receives the running transcript (final + interim) on every result; called with '' when the user starts a new recording.
export default function useSpeechRecognition({ language, active, onTranscript }) {

    const [isListening, setIsListening] = useState(false)
    const [supported, setSupported] = useState(false)
    const [micDenied, setMicDenied] = useState(false)

    const recognitionRef = useRef(null)
    const keepListeningRef = useRef(false) // true while user is recording — drives auto-restart in onend
    const finalTranscriptRef = useRef('') // final results carried across browser-side auto-restarts
    const onTranscriptRef = useRef(onTranscript) // ref so the recognition setup doesn't re-run on every parent render
    useEffect(() => { onTranscriptRef.current = onTranscript }, [onTranscript])

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!SpeechRecognition) return
        setSupported(true)
        const recognition = new SpeechRecognition()
        recognition.lang = SPEECH_LANGS[language] || 'de-DE'
        recognition.interimResults = true
        recognition.continuous = true // don't auto-stop on first pause — restart on onend if user still wants to record
        recognition.onstart = () => setIsListening(true) // browser confirmed mic is live — flip UI now so a denied/failed start doesn't leave us stuck in "listening"
        recognition.onresult = (event) => {
            // only walk new results since resultIndex; older ones are already folded into finalTranscriptRef (also covers post-restart sessions where event.results resets)
            let interim = ''
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const r = event.results[i]
                if (r.isFinal) finalTranscriptRef.current += r[0].transcript
                else interim += r[0].transcript
            }
            onTranscriptRef.current?.(finalTranscriptRef.current + interim)
        }
        recognition.onend = () => {
            // Chrome silently ends the session every ~60s even with continuous=true — restart while the user is still recording
            if (keepListeningRef.current) {
                try { recognition.start() }
                catch (e) {
                    console.error('[useSpeechRecognition] start() on auto-restart failed:', e)
                    keepListeningRef.current = false
                    setIsListening(false)
                }
            } else {
                setIsListening(false)
            }
        }
        recognition.onerror = (event) => {
            if (event.error === 'no-speech' || event.error === 'aborted') return // benign — onend fires next and handles restart vs. stop
            console.error('[useSpeechRecognition] error:', event.error)
            keepListeningRef.current = false // permission/hardware error — stop the restart loop
            setIsListening(false)
            if (event.error === 'not-allowed') setMicDenied(true)
        }
        recognitionRef.current = recognition
        return () => {
            keepListeningRef.current = false // prevent onend from restarting a torn-down recognizer
            recognition.abort()
        }
    }, [language])

    // stop recording when the host disables it (e.g. widget closes) so the mic isn't held open in the background, and drop any stale mic-denied alert so it doesn't reappear on next open
    useEffect(() => {
        if (active) return
        setMicDenied(false)
        if (!isListening) return
        keepListeningRef.current = false
        recognitionRef.current?.abort()
        setIsListening(false)
    }, [active, isListening])

    const toggle = useCallback(() => {
        const recognition = recognitionRef.current
        if (!recognition) return
        if (isListening) {
            keepListeningRef.current = false // user-initiated stop — don't restart in onend
            recognition.stop() // stop (not abort) so the trailing audio still becomes a final result
        } else {
            finalTranscriptRef.current = ''
            onTranscriptRef.current?.('') // clear any pending input as a new session starts
            keepListeningRef.current = true
            setMicDenied(false) // clear any stale denial alert from a previous attempt
            try { recognition.start(); setIsListening(true) }
            catch (e) {
                console.error('[useSpeechRecognition] start() failed:', e)
                keepListeningRef.current = false
                setIsListening(false)
            }
        }
    }, [isListening])

    return { isListening, supported, micDenied, setMicDenied, toggle }
}
